import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { configKeys } from "@/lib/query";
import { type Region, regionArraySchema, regionSchema } from "@/schemas/config";

type FormData = {
  regionName: string;
  agencyTeam: string;
};

const emptyForm: FormData = {
  regionName: "",
  agencyTeam: "",
};

export function RegionPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);

  const {
    data: regions,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: configKeys.regions,
    queryFn: () => apiGet("/api/config/regions", regionArraySchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => apiSend("POST", "/api/config/regions", data, regionSchema),
    onSuccess: () => {
      toast.success("Region created");
      queryClient.invalidateQueries({ queryKey: configKeys.regions });
      setEditingId(null);
      setFormData(emptyForm);
    },
    onError: (error) => {
      logger.error("region_create_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to create region");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) =>
      apiSend("PUT", `/api/config/regions/${id}`, data, regionSchema),
    onSuccess: () => {
      toast.success("Region updated");
      queryClient.invalidateQueries({ queryKey: configKeys.regions });
      setEditingId(null);
      setFormData(emptyForm);
    },
    onError: (error) => {
      logger.error("region_update_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to update region");
    },
  });

  function handleAdd() {
    setEditingId("new");
    setFormData(emptyForm);
  }

  function handleEdit(region: Region) {
    setEditingId(region.id);
    setFormData({
      regionName: region.regionName,
      agencyTeam: region.agencyTeam,
    });
  }

  function handleSubmit() {
    if (editingId === "new") {
      createMutation.mutate(formData);
    } else if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    }
  }

  function handleCancel() {
    setEditingId(null);
    setFormData(emptyForm);
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  function renderFormRow(key: string) {
    return (
      <tr key={key} className="border-b">
        <td className="py-3 pr-4">
          <Input
            value={formData.regionName}
            onChange={(e) => setFormData({ ...formData, regionName: e.target.value })}
            placeholder="Region name"
          />
        </td>
        <td className="py-3 pr-4">
          <Input
            value={formData.agencyTeam}
            onChange={(e) => setFormData({ ...formData, agencyTeam: e.target.value })}
            placeholder="Agency team"
          />
        </td>
        <td className="py-3">
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSubmit} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Regions</h1>
        <div className="mt-4 space-y-2">
          {["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
            <div key={k} className="h-9 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    logger.error("Failed to load regions", { error });
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Regions</h1>
        <ErrorState message="Couldn't load regions." onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Regions</h1>
          <p className="mt-1 text-muted-foreground">
            Define regional assignments and agency team mappings.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={editingId !== null}>
          <Plus className="size-4" />
          Add Region
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 pr-4 font-medium">Region Name</th>
                  <th className="pb-3 pr-4 font-medium">Agency Team</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {regions?.map((region) =>
                  editingId === region.id ? (
                    renderFormRow(region.id)
                  ) : (
                    <tr key={region.id} className="border-b">
                      <td className="py-3 pr-4">{region.regionName}</td>
                      <td className="py-3 pr-4">{region.agencyTeam}</td>
                      <td className="py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Edit"
                          title="Edit"
                          onClick={() => handleEdit(region)}
                          disabled={editingId !== null}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  ),
                )}
                {editingId === "new" && renderFormRow("new")}
              </tbody>
            </table>
            {regions?.length === 0 && editingId !== "new" && (
              <p className="py-8 text-center text-muted-foreground">
                No regions configured. Click "Add Region" to create one.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
